import * as React from 'react'
import { ApolloProvider } from '@apollo/client'
import {
  Route,
  Routes,
  useNavigate,
  NavigateFunction,
  useLocation,
} from 'react-router-dom'

import { User } from 'oidc-client-ts'
import { AuthProvider, useAuth } from 'react-oidc-context'

import { Dashboard } from '@kistill/client/features/dashboard'
import {
  AdminWarning,
  ApiContext,
  FilterItemNumberProvider,
  PageLoading,
  ProfileProvider,
  SelectedGuidsProvider,
} from '@kistill/client/shared/ui'

import { Layout } from './Layout'
import { client } from '../utils/apolloClientSetup'
import { listItems } from './paths'
import { environment } from '../environments/environment'
import { ADMIN_ROUTE } from '@kistill/client/shared/ui'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from './ErrorFallback'
import { datadogLogs } from '@datadog/browser-logs'
import { ListViewProvider } from '@kistill/client/shared/ui'

const Origins = React.lazy(() => import('@kistill/client/features/origins'))
const Customers = React.lazy(() => import('@kistill/client/features/customers'))
const ItemCollections = React.lazy(
  () => import('@kistill/client/features/item-collections'),
)
const Items = React.lazy(() => import('@kistill/client/features/items'))
const ContentAnalyses = React.lazy(
  () => import('@kistill/client/features/content-analysis'),
)
const ItemTitles = React.lazy(
  () => import('@kistill/client/features/item-titles'),
)
const OriginalTitles = React.lazy(
  () => import('@kistill/client/features/original-titles'),
)
const Credits = React.lazy(() => import('@kistill/client/features/credits'))
const AdminDashboard = React.lazy(
  () => import('@kistill/client/features/admin-dashboard'),
)
const Logout = React.lazy(() => import('@kistill/client/features/logout'))

export function App() {
  const navigate = useNavigate()
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AuthProvider
        {...environment.auth}
        onSigninCallback={(user) => onCallback(user, navigate)}
      >
        <ApolloProvider client={client}>
          <ApiContext.Provider value={environment.apiPath}>
            <ProfileProvider>
              <ListViewProvider>
                <FilterItemNumberProvider>
                  <SelectedGuidsProvider>
                    <Routes>
                      <Route
                        path='/'
                        element={
                          <RequireAuth>
                            <Layout listItems={listItems} />
                          </RequireAuth>
                        }
                      >
                        <Route
                          index
                          element={
                            <RequireAuth>
                              <Dashboard />
                            </RequireAuth>
                          }
                        />
                        <Route path='/signin-oidc' />
                        <Route
                          path='uppruni/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <Origins />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='adilar/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <Customers />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='adfang/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <ItemCollections />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='eintak/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <Items />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='efnisgreining/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <ContentAnalyses />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='frumtitill/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <OriginalTitles />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='titill/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <ItemTitles />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />

                        <Route
                          path='kredit/*'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <Credits />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path={`${ADMIN_ROUTE}/*`}
                          element={
                            <RequireAuth>
                              <RequireAdmin>
                                <React.Suspense fallback={<PageLoading />}>
                                  <AdminDashboard />
                                </React.Suspense>
                              </RequireAdmin>
                            </RequireAuth>
                          }
                        />
                        <Route
                          path='utskra'
                          element={
                            <RequireAuth>
                              <React.Suspense fallback={<PageLoading />}>
                                <Logout />
                              </React.Suspense>
                            </RequireAuth>
                          }
                        />

                        <Route
                          path='/signin-oidc'
                          element={<div>Áframsendi á vef...</div>}
                        />
                      </Route>
                    </Routes>
                  </SelectedGuidsProvider>
                </FilterItemNumberProvider>
              </ListViewProvider>
            </ProfileProvider>
          </ApiContext.Provider>
        </ApolloProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export function RequireAdmin({ children }: { children: JSX.Element }) {
  const auth = useAuth()
  const userRole = auth?.user?.profile['user_role']
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  if (!isAdmin) return <AdminWarning />
  else return children
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = useAuth()
  const { pathname } = useLocation()

  if (auth.isLoading) {
    return <PageLoading />
  }

  if (!auth.isAuthenticated && !auth.activeNavigator) {
    auth.signinRedirect({ state: pathname })
  }
  return children
}

const onCallback = async (user: User | void, navigate: NavigateFunction) => {
  let returnUrl = '/'
  if (user) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { access_token, id_token, ...userToLog } = user
    datadogLogs.logger.info('User logged in', userToLog)

    returnUrl = user.state ? (user.state as string) : '/'
  }
  navigate(returnUrl)
}

export default App
